import React, {
  useMemo, useContext, useState, useEffect, useCallback, useRef
} from 'react';
import proptypes from 'prop-types';
const { func, string, shape: propShape, bool } = proptypes;
import classNames from 'classnames';
import {
  QueryProvider, extend, useDataModel,
  arrayOf,
  number as numberType,
  string as stringType,
  shape as shapeType,
  customType,
  params,
} from '@thd-nucleus/data-sources';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { Button } from '@one-thd/sui-atomic-components';
import { ExperienceContext, useConfigService, useStore } from '@thd-nucleus/experience-context';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { GroupAccordion } from '@thd-olt-component-react/group-accordion';
import { AddTo } from '@thd-olt-component-react/add-to';
import { QuickEstimate } from '@thd-olt-component-react/pro-planning-tools';
import {
  INCREMENT_QUANTITY,
  REMOVE_PRODUCT,
  PAGE_SIZE,
  createIntentGroups,
  isIntentId, groupToBeOpened,
  getIntentForReplace
} from '../productListHelper';
import { ProductListContext } from '../ProductListContext';
import { ListPod } from './ListPod';
import { ListPodMobile } from './ListPodMobile';
import { ActionBar } from '../action-bar';
import { StickyActionObserver } from '../action-bar/StickyActionObserver';
import { CreateGroup } from '../create-group/CreateGroup';
import { NoteButton } from '../note/NoteButton';
import { DeleteButton } from '../delete-button/DeleteButton';
import { ReplaceButton } from '../replace-button/ReplaceButton';
import { MoreMenu } from '../more-menu/MoreMenu';
import { EditGroup } from '../edit-group/EditGroup';
import { GroupSubTitle } from './GroupSubTitle';
import { ProductLoader } from '../ProductLoader';
import { IntentPod } from './IntentPod';
import { useItemGroups } from '../hooks/useItemGroups';
import { IntentInput } from '../intent-input/IntentInput';
import { useObserverLoadQueue } from '../hooks/useObserverLoadQueue';

const ListGroupWrapper = ({ children }) => {
  return children;
};

ListGroupWrapper.dataModel = extend(ListPod, ListPodMobile, GroupSubTitle, NoteButton);

export const ListView = (props) => {
  const {
    storeId,
    skipFn,
    productDefaultVariables,
    mountedFn,
    setIntersecting,
    groupTotals,
    setGroupTotals
  } = props;
  const {
    list,
    isSharedList,
    productsDisplayed,
    onEditSuccess,
    setProductsDisplayed,
    onSuccesfulMaterialsAdd,
    refetch,
    clearListFromCache,
    productInfoLoading,
    loadItemsOnDemand,
    setExpandedGroups,
    resolvedIntentItemId,
    // Replace feature state
    isReplaceMode,
    replacingItemId,
    replacingGroupId,
    replacingIntentId,
    replacingIntent,
    replacingQuantity
  } = useContext(ProductListContext);

  const [groupProductMap, setGroupProductMap] = useState({});
  const [itemOrderInitialized, setItemOrderInitialized] = useState(false);
  const [currentListId, setCurrentListId] = useState(null);
  const [isExpandAllInProgress, setIsExpandAllInProgress] = useState(false);
  const [errorSelectionEmpty, setErrorSelectionEmpty] = useState(null);
  const [hasItemsSelected, setHasItemsSelected] = useState(false);
  const { isB2BCustomer = false } = useThdCustomer();
  const fsQuickEstimateEnabled = useConfigService('fs:isQuickEstimateEnabled') || false;

  const noteButtonRefs = useRef({});

  const updateErrorSelectionEmpty = useCallback((error) => {
    setErrorSelectionEmpty(error);
  }, []);

  // Queue system for sequential quantity updates
  const [updateQueue, setUpdateQueue] = useState([]);
  const [isProcessingQueue, setIsProcessingQueue] = useState(false);
  const [rearrangeData, setRearrangeData] = useState(null);

  const materials = useMemo(() => {
    return list?.groups.map((group) => {
      return {
        groupId: group.groupId,
        groupName: group.groupName,
        items: group.items?.map(((item) => {
          const productData = item.itemId ? productsDisplayed.find(
            (__pd) => __pd.itemId === item.itemId
          ) || null : null;
          const itemData = {
            itemId: item.itemId,
            intent: item.intent,
            quantity: item.quantity,
            name: null,
            brand: null
          };
          if (productData) {
            itemData.name = productData?.identifiers?.productLabel;
            itemData.brand = productData?.identifiers?.brandName;
          }
          return itemData;
        }))
      };
    });
  }, [list, productsDisplayed]);

  // Reset itemOrder initialization flag when list changes
  useEffect(() => {
    if (list?.id && list?.id !== currentListId) {
      setCurrentListId(list?.id);
      setItemOrderInitialized(false);
    }
  }, [list?.id, currentListId]);

  // Reset selection state when list changes to prevent stale state
  useEffect(() => {
    setHasItemsSelected(false);
  }, [list?.id]);

  const { channel } = useContext(ExperienceContext);
  const isMobile = channel === 'mobile';

  // Stable default group object to prevent unnecessary re-renders
  const defaultGroupTemplate = useMemo(() => ({
    groupId: null,
    groupName: null,
    groupOrder: null,
    groupSubtotal: null,
    items: [],
    title: 'Default Group'
  }), []);

  // Simplified groups management to prevent infinite loops
  const groups = useMemo(() => {
    const listGroups = list?.groups || [];

    // Separate default group and other groups
    const defaultGroup = listGroups.find((group) => group.groupId === null || group.groupId === '__ungrouped__');
    const otherGroups = listGroups.filter((group) => group.groupId !== null && group.groupId !== '__ungrouped__');

    // Use existing default group or stable template
    const ensuredDefaultGroup = defaultGroup || defaultGroupTemplate;

    // Always put default group first, followed by other groups
    const result = [ensuredDefaultGroup, ...otherGroups];

    return result;
  }, [list?.groups, defaultGroupTemplate]);

  // Observer load queue hook - manages sequential placeholder loading
  // Note: groupedItems passed after useItemGroups creates circular dep,
  // so hook uses groups directly and computes notLoadedItems internally
  const {
    loadingItemIds,
    setLoadingItemIds,
    handleObserverIntersect
  } = useObserverLoadQueue({
    groups,
    loadItemsOnDemand,
    productsDisplayed,
    listId: list?.id
  });

  const { groupedItems, setGroupedItems } = useItemGroups({
    groups, productsDisplayed, groupTotals, loadingItemIds, resolvedIntentItemId
  });

  const [listOperations] = useDataModel('listOperations', {
    variables: {
      id: list?.id
    },
    fetchPolicy: 'no-cache',
    context: { withAuth: true },
    lazy: true
  });

  // Remove the manual priceSubtotal fetching - GroupSubTitle will handle it
  // Following project-details pattern where GroupSubTitle is self-contained

  // Callback function for GroupSubTitle to report totals (project-details pattern)
  const onGroupTotalAvailable = useCallback((groupId, total) => {
    setGroupTotals((prevTotals) => {
      const newTotals = { ...prevTotals };
      if (total === 0) {
        // Check if group is actually empty
        const catalogItems = groupProductMap[groupId]?.filter((item) => item.type === 'catalog');
        const isEmptyGroup = catalogItems?.length === 0;
        if (isEmptyGroup) {
          delete newTotals[groupId];
          return newTotals;
        }
      }
      return {
        ...prevTotals,
        [groupId]: total
      };
    });
  }, [groupProductMap]);

  // Update groupProductMap when groups or productsDisplayed change
  useEffect(() => {
    const newGroupProductMap = {};

    if (groups?.length) {
      groups.forEach((group) => {
        const groupProducts = group.items || [];
        // Handle ungrouped items (groupId is null)
        const groupKey = group.groupId || '__ungrouped__';

        // Filter out intent items (items without itemId) from groupProductMap
        newGroupProductMap[groupKey] = groupProducts
          .filter((item) => item.itemId) // Exclude intent items
          .map((item) => ({
            itemId: item.itemId,
            quantity: item.quantity,
            type: 'catalog' // Default to catalog type
          }));
      });

      setGroupProductMap(newGroupProductMap);

      // Clean up group totals for deleted groups - use functional update to avoid dependency
      const newGroupProductMapGroupIds = Object.keys(newGroupProductMap);
      setGroupTotals((prevTotals) => {
        const deletedGroupIds = Object.keys(prevTotals).filter(
          (groupId) => !newGroupProductMapGroupIds.includes(groupId)
        );
        if (deletedGroupIds.length) {
          const newTotals = { ...prevTotals };
          deletedGroupIds.forEach((groupId) => {
            delete newTotals[groupId];
          });
          return newTotals;
        }
        return prevTotals;
      });
    } else {
      // No groups, but we should never get here now due to the default group logic above
      setGroupProductMap({});
      setGroupTotals({});
    }
  }, [groups]); // Removed groupTotals from dependency array to prevent infinite loop

  // Phase 1: Initialize itemOrder for ungrouped items on initial render
  useEffect(() => {
    // Only run once per list and only if we haven't already initialized itemOrder
    if (itemOrderInitialized || !list?.id) {
      return;
    }

    // Check if ungrouped items need itemOrder assignment
    const ungroupedGroup = groups.find((group) => group.groupId === null);

    if (ungroupedGroup && ungroupedGroup.items?.length > 0) {
      const itemsNeedingOrder = ungroupedGroup.items.filter((item) => !item.itemOrder || item.itemOrder === 0
      );

      if (itemsNeedingOrder.length > 0) {
        // Mark as initialized to prevent future calls
        setItemOrderInitialized(true);

        // Call UpdateItems to assign sequential itemOrder
        const updatedGroups = groups.map((group) => {
          if (group.groupId === null) {
            return {
              groupId: null,
              groupName: group.groupName,
              groupOrder: group.groupOrder?.toString(),
              products: group.items.map((item, index) => ({
                itemId: item.itemId,
                quantity: item.quantity,
                itemOrder: index + 1, // Assign sequential order
                comments: item.comments || ''
              }))
            };
          }
          // Return other groups unchanged for this operation
          return {
            groupId: group.groupId,
            groupName: group.groupName,
            groupOrder: group.groupOrder?.toString(),
            products: (group.items || []).map((item) => ({
              itemId: item.itemId,
              quantity: item.quantity,
              itemOrder: item.itemOrder,
              comments: item.comments || ''
            }))
          };
        });

        // Call UpdateItems with fixed itemOrder
        listOperations({
          variables: {
            operationName: 'UpdateItems',
            id: list?.id,
            groups: updatedGroups.filter((group) => group.products?.length > 0)
          }
        }).catch((error) => {
          // Reset flag on error so we can retry
          setItemOrderInitialized(false);
          // eslint-disable-next-line no-console
          console.error('Error initializing itemOrder for ungrouped items:', error);
        });
      } else {
        // All items already have proper itemOrder, mark as initialized
        setItemOrderInitialized(true);
      }
    } else {
      // No ungrouped items or empty group, mark as initialized
      setItemOrderInitialized(true);
    }
  }, [groups, list?.id, itemOrderInitialized, listOperations]);

  // Queue processor - processes updates sequentially
  useEffect(() => {
    const processQueue = async () => {
      if (isProcessingQueue || updateQueue.length === 0) {
        return;
      }

      setIsProcessingQueue(true);

      try {
        // Process the first item in the queue
        const nextUpdate = updateQueue[0];

        // Get fresh groups data at the time of processing
        const currentGroups = list?.groups || [];
        const freshGroups = currentGroups.length > 0 ? currentGroups : [defaultGroupTemplate];

        // Find the group and item to update
        const targetGroup = freshGroups.find((group) => group.items?.some((item) => item.itemId === nextUpdate.itemId)
        );

        if (!targetGroup) {
          // Remove this update from queue and continue
          setUpdateQueue((prev) => prev.slice(1));
          setIsProcessingQueue(false);
          return;
        }

        // Create updated groups with the new quantity
        const updatedGroups = freshGroups.map((group) => {
          return {
            groupId: group.groupId,
            groupName: group.groupName,
            groupOrder: group.groupOrder?.toString(),
            products: (group.items || []).map((item) => ({
              itemId: item.itemId,
              quantity: item.itemId === nextUpdate.itemId ? nextUpdate.newQuantity.quantity : item.quantity,
              itemOrder: item.itemOrder || 1,
              comments: item.comments || ''
            }))
          };
        });

        // Execute the mutation
        const response = await listOperations({
          variables: {
            operationName: 'UpdateItems',
            id: list?.id,
            groups: updatedGroups
          }
        });

        // Trigger analytics
        const products = [{
          itemId: nextUpdate.itemId,
          quantity: nextUpdate.newQuantity.quantity,
          price: String(nextUpdate.price)
        }];

        const isIncrement = nextUpdate.quantity < nextUpdate.newQuantity.quantity;
        if (isIncrement) {
          window.LIFE_CYCLE_EVENT_BUS.trigger(INCREMENT_QUANTITY, { products });
        } else {
          window.LIFE_CYCLE_EVENT_BUS.trigger(REMOVE_PRODUCT, { products });
        }

        onEditSuccess({});

        // Refetch to get latest data
        if (response?.data) {
          await refetch();
        }

      } catch (error) {
        // Still refetch on error to maintain consistency
        await refetch();
      }

      // Remove the processed item from queue
      setUpdateQueue((prev) => prev.slice(1));
      setIsProcessingQueue(false);
    };

    processQueue();
  }, [updateQueue, isProcessingQueue, list, defaultGroupTemplate, listOperations, refetch, onEditSuccess]);

  useEffect(() => {
    if (rearrangeData) {
      const groupsToRearrange = [...rearrangeData];
      setRearrangeData(null);

      const performRearrangement = async (__groupsToRearrange) => {
        await listOperations({
          variables: {
            operationName: 'UpdateItems',
            id: list?.id,
            groups: __groupsToRearrange
          }
        });
        // Manually refetch after moving items between groups
        await refetch();
        // Also clear the main list cache to ensure consistency
        await clearListFromCache(list?.id);
        onEditSuccess({});
      };
      const withProducts = groupsToRearrange.filter((__group) => (__group.products || []).length > 0);
      if (withProducts?.length > 0) {
        performRearrangement(withProducts);
      }
    }
  }, [rearrangeData, listOperations, refetch, clearListFromCache, onEditSuccess]);

  const onDragEnd = useCallback(async (dragObj) => {
    const {
      prevGroupId, item: id, newGroupId, newIndex, prevIndex, groupedItems: currentGroupedItems
    } = dragObj;

    const isIntent = isIntentId(id);
    // Update local state immediately for better UX
    setGroupedItems((prevItems) => {
      return currentGroupedItems.map((currentGroup) => {
        const groupInfo = prevItems.find((grp) => grp.id === currentGroup.id);
        return {
          ...groupInfo,
          items: currentGroup.items
        };
      });
    });

    // Only make API call if there was an actual change
    if (!(prevGroupId === newGroupId && prevIndex === newIndex)) {
      // Find the item being moved to get its current quantity and comments
      const itemToMove = productsDisplayed.find((product) => (
        product.itemId === id || (isIntent && product.intentId === id)
      ));
      const itemQuantity = itemToMove?.quantity || 1;
      const itemComments = itemToMove?.comments || '';
      const itemIntentId = itemToMove?.intentId || null;

      try {
        // Moving between regular groups or within a group - use listOperations
        const actualNewGroupId = newGroupId === '__ungrouped__' ? null : newGroupId;
        const actualPrevGroupId = prevGroupId === '__ungrouped__' ? null : prevGroupId;

        // Create a copy of existing groups
        let updatedGroups = list?.groups?.filter(
          (group) => [actualNewGroupId, actualPrevGroupId].includes(group.groupId)
        );

        const movedItem = {
          itemId: isIntent ? null : id,
          quantity: itemQuantity,
          itemOrder: newIndex + 1,
          comments: itemComments,
          intentId: itemIntentId
        };
        // Now process all groups with guaranteed Default Group existence
        updatedGroups = updatedGroups.map((group) => {
          const currentGroupId = group.groupId;
          const newItems = [...(group.items || [])];

          if (currentGroupId === actualNewGroupId) {
            if (currentGroupId === actualPrevGroupId) {
              const oldIndex = newItems.findIndex((item) => (
                item.itemId === id || (isIntent && item.intentId === id)
              ));
              if (oldIndex !== -1) {
                newItems.splice(oldIndex, 1);
              }
            }
            // Add item to the new group at the specified index
            newItems.splice(newIndex, 0, movedItem);
          } else if (currentGroupId === actualPrevGroupId) {
            const oldIndex = newItems.findIndex((item) => (
              item.itemId === id || (isIntent && item.intentId === id)
            ));
            if (oldIndex !== -1) {
              newItems.splice(oldIndex, 1);
            }
          }

          return {
            groupId: currentGroupId,
            products: newItems.map((__item, __index) => ({
              itemId: __item.itemId,
              intentId: __item.intentId,
              itemOrder: __index + 1
            }))
          };
        });

        // Ensure we include all affected groups in the mutation
        const groupsForMutation = updatedGroups.map((group) => {
          const prevGroupItems = list?.groups?.find((__g) => __g.groupId === group.groupId)?.items || [];
          return {
            ...group,
            products: group.products?.filter((__groupItem) => {
              if (prevGroupItems.length === 0) return true;
              const __prevGroupItem = prevGroupItems.find((item) => {
                return (item.itemId !== null && item.itemId === __groupItem.itemId)
                  || (item.itemId === null && item.intentId === __groupItem.intentId);
              });
              // Only include items that have changed position
              return !__prevGroupItem || __prevGroupItem.itemOrder !== __groupItem.itemOrder;
            })
          };
        });
        setRearrangeData(groupsForMutation);
      } catch (error) {
        // Optionally revert local state on error
        // eslint-disable-next-line no-console
        console.error('Error updating item position:', error);
      }
    }
  }, [
    productsDisplayed, list?.groups, list?.id, setProductsDisplayed
  ]);

  const handleQuantityUpdate = ({ itemId, price, quantity, newQuantity }) => {
    // Add the update to the queue instead of processing immediately
    const updateRequest = {
      itemId,
      price,
      quantity,
      newQuantity,
      timestamp: Date.now()
    };

    setUpdateQueue((prev) => {
      // Remove any existing updates for the same item (replace with latest)
      const filteredQueue = prev.filter((update) => update.itemId !== itemId);
      const newQueue = [...filteredQueue, updateRequest];

      return newQueue;
    });
  };

  const renderListPod = useCallback(({ groupId, id: itemId }) => {
    // Safety check for valid inputs
    if (!itemId || !groupId || !groups || !Array.isArray(groups)) {
      return null;
    }

    // Handle ungrouped items - convert frontend ID back to null for lookup
    const actualGroupId = groupId === '__ungrouped__' ? null : groupId;
    const group = groups.find((grp) => grp?.groupId === actualGroupId);

    if (!group) return null;

    const listItem = group?.items?.find((groupItem) => {
      // Check for intent items by intentId (backend ID)
      if (groupItem?.intentId === itemId) {
        return true;
      }
      // Check for regular catalog items by itemId
      return groupItem?.itemId === itemId;
    });

    if (!listItem) return null;

    // Get additional product info from productsDisplayed for availabilityType
    const productInfo = productsDisplayed.find((product) => product?.itemId === itemId);

    // CRITICAL: Only render if product data is available to prevent individual 'product' calls
    // Intent items have intentId and don't need productInfo, catalog items need productInfo
    // Return null to let GroupAccordion render its internal GroupItemPlaceHolder automatically
    if (!productInfo && !listItem?.intentId) {
      return null;
    }

    // Validate product has minimum required data before rendering
    // Check that productInfo has essential itemId field (basic validation)
    if (productInfo && !productInfo.itemId) {
      return null; // Product data incomplete, let GroupAccordion render placeholder
    }

    // Don't render if item is currently being loaded on-demand (prevents stuck loading states)
    // Return null to let GroupAccordion render its internal GroupItemPlaceHolder automatically
    if (loadingItemIds.has(itemId) && !isIntentId(itemId)) {
      return null;
    }
    const showStoreSku = productInfo?.availabilityType?.type !== 'Online';

    // Check if this item is currently being replaced - render IntentPod for search/selection
    if (isReplaceMode && replacingItemId === itemId) {
      // Determine if this is a real intent being replaced (has valid intentId) or a regular product
      // For regular products, we create an ephemeral/fake intentId that shouldn't be sent to backend
      const hasRealIntent = !!replacingIntentId;
      const ephemeralIntentId = `replace-${itemId}`;

      // Calculate intent value - filter out empty strings, use product label as fallback
      const fallbackIntent = getIntentForReplace(listItem, productInfo);
      const finalIntent = (replacingIntent && replacingIntent.trim()) 
        || (listItem?.intent && listItem?.intent.trim())
        || fallbackIntent;

      return (
        <ErrorBoundary name="replace-intentPod-boundary">
          <IntentPod
            groupId={replacingGroupId}
            intentId={replacingIntentId ?? ephemeralIntentId}
            intent={finalIntent}
            quantity={replacingQuantity ?? listItem?.quantity ?? 1}
            itemOrder={listItem?.itemOrder}
            storeId={storeId}
            isReplaceModeItem
            hasRealIntent={hasRealIntent}
            replacingItemId={itemId}
          />
        </ErrorBoundary>
      );
    }

    if (isIntentId(itemId) || (listItem?.intent?.trim() && !listItem?.itemId)) {
      // Use intent text with product label fallback (same pattern as replace mode)
      const intentText = listItem?.intent?.trim() || getIntentForReplace(listItem, productInfo);
      return (
        <ErrorBoundary name="intentPod-boundary">
          <IntentPod
            groupId={groupId}
            intentId={listItem?.intentId}
            intent={intentText}
            quantity={listItem.quantity}
            itemOrder={listItem.itemOrder}
            storeId={storeId}
          />
        </ErrorBoundary>
      );
    }

    return (
      <ErrorBoundary name="list-pod-boundary">
        {
          isMobile ? (
            <ListPodMobile
              storeId={storeId}
              itemId={itemId}
              quantity={listItem.quantity}
              handleQuantityUpdate={handleQuantityUpdate}
              searchKeyword={listItem.intent}
              showStoreSku={showStoreSku}
            />
          ) : (
            <ListPod
              storeId={storeId}
              itemId={itemId}
              quantity={listItem.quantity}
              handleQuantityUpdate={handleQuantityUpdate}
              searchKeyword={listItem.intent}
              showStoreSku={showStoreSku}
            />
          )
        }
      </ErrorBoundary>
    );
  }, [groups, productsDisplayed, isMobile, storeId, handleQuantityUpdate, loadingItemIds,
    isReplaceMode, replacingItemId, replacingGroupId, replacingIntentId, replacingIntent, replacingQuantity]);

  const renderIntentInput = useCallback(({ groupId }) => {
    // Safety checks
    if (!groupId || !groups || !Array.isArray(groups) || isSharedList) return null;

    // Handle regular groups - convert frontend ID back to null for lookup
    const actualGroupId = groupId === '__ungrouped__' ? null : groupId;
    const listGroup = groups.find((grp) => grp?.groupId === actualGroupId);
    if (!listGroup) return null;

    return (
      <IntentInput
        groupId={groupId}
      />
    );
  }, [groups, isSharedList]);

  const renderGroupButtons = useCallback((group) => {
    // Safety checks
    if (!group || !groups || !Array.isArray(groups)) return null;

    // Don't show edit buttons for Default Group (ungrouped items)
    if (group.title === 'Default Group') return null;

    // Handle regular groups - convert frontend ID back to null for lookup
    const actualGroupId = group?.id === '__ungrouped__' ? null : group?.id;
    const listGroup = groups.find((grp) => grp?.groupId === actualGroupId);
    if (!listGroup) return null;

    return (
      <div className="sui-flex sui-flex-row sui-gap-2">
        <QueryProvider cacheKey={`edit-group-${group?.id}`}>
          <EditGroup
            groupId={group?.id}
            groupName={group.title}
            groupOrder={listGroup.groupOrder}
          />
        </QueryProvider>
      </div>
    );
  }, [groups]);

  const renderSubtitle = useCallback((group) => {
    // Safety checks
    if (!group || !groupProductMap) return null;

    const groupKey = group.groupId || group?.id;
    const groupedItem = groupedItems.find((item) => item.id === groupKey);

    const listGroups = list?.groups || [];

    // Following project-details customSubTitle pattern
    // GroupAccordion transforms the id to groupId, so use group.groupId instead of group?.id
    const products = groupProductMap[groupKey] || [];
    const groupDetails = listGroups.find((listGroup) => group.groupId === (listGroup.groupId || '__ungrouped__'));

    // Simple key that ensures re-render when product count changes (especially 0)
    const componentKey = `${groupKey}-${products.length}`;

    return (
      <GroupSubTitle
        key={componentKey}
        groupId={groupKey}
        totalItems={groupDetails?.items?.length || 0}
        products={products}
        onGroupTotalAvailable={onGroupTotalAvailable}
        isMobile={isMobile}
        isLoading={false} // GroupSubTitle manages its own loading
        isDefault={groupedItem?.isDefault} // Hide UI for default group but still report totals
      />
    );
  }, [groupProductMap, onGroupTotalAvailable, isMobile, list?.groups, groupedItems]);

  const renderDeleteButton = useCallback((itemKey) => {
    if (isSharedList) {
      return null;
    }

    const formattedItemKey = itemKey.includes('-$-') ? itemKey.split('-$-')[0] : itemKey;

    // Find the item in list.groups to get additional context for MoreMenu
    const listItemWithGroup = list?.groups
      ?.filter((group) => group.items)
      ?.map((group) => {
        const foundItem = group.items.find(
          (item) => item.itemId === formattedItemKey || item.intentId === formattedItemKey
        );
        return foundItem ? { item: foundItem, groupId: group.groupId } : null;
      })
      ?.find((result) => result) || null;

    const listItem = listItemWithGroup?.item || null;
    const groupId = listItemWithGroup?.groupId || null;
    const hasNote = !!listItem?.comments;
    const intentId = listItem?.intentId || null;
    const itemOrder = listItem?.itemOrder || null;
    const quantity = listItem?.quantity || 1;
    const intent = listItem?.intent || null;

    // Detect if this is an intent pod (pure intent with no itemId)
    const isIntentPod = !formattedItemKey || formattedItemKey === intentId;

    // Get product info to check if hidden
    const productInfo = productsDisplayed.find((product) => product?.itemId === formattedItemKey);
    const isHiddenProduct = productInfo?.info?.isHidden || false;

    // Get intent text for replace - use product label if no original intent
    const replaceIntent = getIntentForReplace(listItem, productInfo);

    // Don't show replace button for hidden products or for intent pods on mobile
    const showReplaceButton = !isHiddenProduct && !isIntentPod;

    // Handler to open note drawer for this item
    const handleOpenNoteDrawer = () => {
      if (noteButtonRefs.current[formattedItemKey]) {
        noteButtonRefs.current[formattedItemKey].click();
      }
    };

    return (
      <div className="sui-flex sui-flex-row sui-items-center sui-gap-2">
        {showReplaceButton && (
          <ReplaceButton
            itemId={formattedItemKey}
            intentId={intentId}
            groupId={groupId}
            intent={replaceIntent}
            quantity={quantity}
          />
        )}
        <DeleteButton iconSize="small" itemId={formattedItemKey} />
        <MoreMenu
          iconSize="small"
          onAddNote={handleOpenNoteDrawer}
          hasNote={hasNote}
          itemId={formattedItemKey}
          intent={intent}
          intentId={intentId}
          groupId={groupId}
          quantity={quantity}
          itemOrder={itemOrder}
          isHiddenProduct={isHiddenProduct}
          isIntentPod={isIntentPod}
        />
        {/* Hidden NoteButton to trigger drawer */}
        <div className="sui-hidden">
          <NoteButton ref={(ref) => { noteButtonRefs.current[formattedItemKey] = ref; }} itemId={formattedItemKey} />
        </div>
      </div>
    );
  }, [isSharedList, list?.groups, productsDisplayed]);

  const renderNoteButton = useCallback((itemKey) => {
    if (isSharedList) {
      return null;
    }

    const formattedItemKey = itemKey.includes('-$-') ? itemKey.split('-$-')[0] : itemKey;

    return (
      <NoteButton
        iconSize="regular"
        itemId={formattedItemKey}
      />
    );
  }, [isSharedList]);

  // Auto-expansion logic following GroupAccordion patterns
  const groupToBeAutoOpened = useMemo(() => {
    return groupToBeOpened(groupedItems);
  }, [groupedItems]);

  // Expand/Collapse functionality following project-details pattern
  const onGroupExpand = useCallback(async (expanded, groupId) => {
    // Track expanded/collapsed state
    setExpandedGroups((prev) => {
      const newSet = new Set(prev);
      if (expanded) {
        newSet.add(groupId);
      } else {
        newSet.delete(groupId);
      }
      return newSet;
    });

    if (expanded && loadItemsOnDemand) {
      // Find the group and its unloaded items
      const group = groupedItems.find((grp) => grp?.id === groupId);
      if (group && group.notLoadedItems && group.notLoadedItems.length > 0) {
        // Limit the number of items to load at once to respect batching (PAGE_SIZE = 6)
        const itemsToLoad = group.notLoadedItems.slice(0, PAGE_SIZE);

        try {
          await loadItemsOnDemand(itemsToLoad);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error loading items for expanded group:', error);
        }
      }
    }

    // Add analytics if needed in the future
    // if (expanded) {
    //   triggerAnalytics('expand-group', { groupId });
    // } else {
    //   triggerAnalytics('collapse-group', { groupId });
    // }
  }, [groupedItems, loadItemsOnDemand, setExpandedGroups]);

  const onExpandAll = useCallback(async () => {
    // Mark all groups as expanded
    const allGroupIds = groupedItems.map((group) => group?.id);
    setExpandedGroups(new Set(allGroupIds));

    // If we have loadItemsOnDemand, batch load all unloaded items starting with the first accordion
    if (loadItemsOnDemand) {
      // Set flag to prevent intersection observers from triggering
      setIsExpandAllInProgress(true);

      // Collect all unloaded items from all groups, maintaining group order
      const allUnloadedItems = [];

      groupedItems.forEach((group) => {
        if (group.notLoadedItems && group.notLoadedItems.length > 0) {
          // Just collect the item IDs in order
          allUnloadedItems.push(...group.notLoadedItems);
        }
      });

      // Load items in batches of PAGE_SIZE sequentially
      if (allUnloadedItems.length > 0) {
        try {
          // Load all items in sequential batches
          for (let currentIndex = 0; currentIndex < allUnloadedItems.length; currentIndex += PAGE_SIZE) {
            const batch = allUnloadedItems.slice(currentIndex, currentIndex + PAGE_SIZE);
            // eslint-disable-next-line no-await-in-loop
            await loadItemsOnDemand(batch);
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error loading items in expand all:', error);
        } finally {
          // Clear the flag after all loading is complete
          setIsExpandAllInProgress(false);
        }
      } else {
        // No items to load, clear the flag immediately
        setIsExpandAllInProgress(false);
      }
    }

    window.LIFE_CYCLE_EVENT_BUS.trigger('product-list.expand-all');
  }, [groupedItems, loadItemsOnDemand, setExpandedGroups]);

  const onCollapseAll = useCallback(() => {
    window.LIFE_CYCLE_EVENT_BUS.trigger('product-list.collapse-all');
  }, [groupedItems]);

  const handleSelectAllGroupsAndItems = () => {
    window.LIFE_CYCLE_EVENT_BUS.trigger('product-list.select-all');
  };

  // Handler for selection changes from GroupAccordion
  // Consolidated selection tracking - runs once per selection change to avoid duplicate state updates
  const handleSelectionChange = useCallback(({ selectedItems }) => {
    // Check if any items are selected across all groups
    const hasSelected = selectedItems && Object.values(selectedItems).some(
      (group) => group?.items?.length > 0 || (group?.isSelected && !group?.isIndeterminate)
    );
    setHasItemsSelected(hasSelected);
  }, []);

  const defaultVariable = useMemo(() => ({
    storeId,
    isBrandPricingPolicyCompliant: true,
    loyaltyMembershipInput: null,
  }), [storeId]);

  const renderProductLoader = ({
    groupItems,
    renderGroupContent,
    groupId,
    groupIndex,
    groupChangeStatus
  }) => {
    if (!groupedItems || !Array.isArray(groupedItems)) {
      return null;
    }

    const group = groupedItems.find((grp) => grp?.id === groupId);

    return (
      <ProductLoader
        renderGroupContent={renderGroupContent}
        group={group}
        groupItems={groupItems}
        groupChangeStatus={groupChangeStatus}
        groupIndex={groupIndex}
        loadItemsOnDemand={loadItemsOnDemand}
        isExpandAllInProgress={isExpandAllInProgress}
        setLoadingItemIds={setLoadingItemIds}
        onObserverIntersect={handleObserverIntersect}
      />
    );
  };

  // eslint-disable-next-line react/prop-types
  const customActionButtons = useMemo(() => ({ selectedItems, onSelectionChange }) => {
    return (
      <QueryProvider
        cacheKey="list-action-bar-regular"
        defaultVariables={defaultVariable}
      >
        <ActionBar
          selectedItemsByGroup={selectedItems}
          onGroupAccordionSelectionChange={onSelectionChange}
          showActionBar // Always show action bar when component is rendered
          errorSelectionEmpty={errorSelectionEmpty}
          updateErrorSelectionEmpty={updateErrorSelectionEmpty}
        />
      </QueryProvider>
    );
  }, [defaultVariable, errorSelectionEmpty, updateErrorSelectionEmpty]);

  const customStickyActionButtons = useMemo(() => (
    // eslint-disable-next-line react/prop-types
    { selectedItems, onSelectionChange }
  ) => {
    return (
      <QueryProvider
        cacheKey="list-action-bar-sticky"
        defaultVariables={defaultVariable}
      >
        <ActionBar
          selectedItemsByGroup={selectedItems}
          // onGroupAccordionSelectionChange={onSelectionChange}
          sticky
          errorSelectionEmpty={errorSelectionEmpty}
          updateErrorSelectionEmpty={updateErrorSelectionEmpty}
        />
      </QueryProvider>
    );
  }, [defaultVariable, errorSelectionEmpty, updateErrorSelectionEmpty]);

  const showBtn = !productsDisplayed.length && !isSharedList && groupedItems.length === 1;
  const buttonWrapperClass = classNames({
    'sui-visible': showBtn,
    'sui-hidden': !showBtn
  });

  // Memoized display conditions for B2B/mobile-specific features
  const showTopAddMaterials = isMobile && isB2BCustomer;
  const showQuickEstimate = isB2BCustomer && fsQuickEstimateEnabled && !isSharedList;
  const showRightAddMaterials = !isMobile || !isB2BCustomer;

  const quickEstimateWrapper = classNames(
    'sui-flex sui-items-center',
    {
      'sui-min-w-64': !isMobile,
      'sui-w-96': isMobile
    }
  );

  const hasListItems = groups?.some((group) => group.items && group.items.length > 0);
  const hasGroups = groups.length > 1;
  const isEmptyList = (list?.itemCount === 0 || !hasListItems) && !hasGroups;

  const handleIntentsChange = (intentGroups) => {
    return createIntentGroups({
      intentGroups,
      list,
      listOperations,
      refetch,
      onEditSuccess
    });
  };

  const renderAddMaterials = () => {
    return (
      <QueryProvider
        cacheKey={`list-add-to-materials-${list?.id || 'default'}`}
        defaultVariables={defaultVariable}
      >
        <AddTo
          addToType="List"
          list={{
            listId: list?.id,
            onMaterialsAdded: onSuccesfulMaterialsAdd,
            isMaterialsButton: true
          }}
          CustomAddToActionElement={({ onClick }) => (
            <Button
              aria-label="Add Materials"
              variant="primary"
              onClick={onClick}
              text="Add Materials"
              disabled={isSharedList}
            >
              Add Materials
            </Button>
          )}
          products={[]}
          aiListBuilderData={{
            onNewIntentsSave: handleIntentsChange,
            title: list?.name,
            materials,
            refType: 'LIST',
            refId: list?.id
          }}
        />
      </QueryProvider>
    );
  };

  const headerButtonWrapperClass = classNames(
    'sui-w-full sui-flex sui-justify-between sui-gap-2',
    {
      "sui-mt-4": !isMobile,
      "sui-mt-2": isMobile
    }
  );

  const rightDivClass = classNames(
    'sui-w-full sui-flex sui-items-center sui-gap-4',
    {
      'sui-justify-between': isMobile,
      'sui-justify-end': !isMobile,
      'sui-py-2': !isB2BCustomer
    }
  );

  return (
    <div className="sui-w-full" data-testid="list-view-component">
      {showTopAddMaterials && (
        <div className="sui-w-full sui-py-2">
          {renderAddMaterials()}
        </div>
      )}
      <div className={headerButtonWrapperClass}>
        {showQuickEstimate && (
          <div className={quickEstimateWrapper}>
            <QuickEstimate materials={materials} />
          </div>
        )}
        <div className={rightDivClass}>
          <QueryProvider
            cacheKey="list-create-group"
            defaultVariables={defaultVariable}
          >
            <CreateGroup storeId={storeId} disabled={isSharedList} />
          </QueryProvider>
          {showRightAddMaterials && renderAddMaterials()}
        </div>
      </div>
      <StickyActionObserver
        setIntersecting={setIntersecting}
      >
        <div /> {/* Empty div for intersection observer */}
      </StickyActionObserver>

      <QueryProvider
        dataSource="catalog"
        cacheKey={`list-product-pods-${isMobile ? 'mobile' : 'desktop'}`}
        skip={skipFn}
        mounted={mountedFn}
        defaultVariables={productDefaultVariables}
        queryOptions={({ options, queryName }) => {
          if (queryName === 'listOperations' || queryName === 'deleteList') {
            return {
              ...options,
              fetchPolicy: 'no-cache'
            };
          }
          return options;
        }}
      >
        <ListGroupWrapper data-testid="list-group-wrapper">
          {
            groupedItems && groupedItems.length >= 0 && (
              <GroupAccordion
                groupedItems={groupedItems}
                onDragEnd={onDragEnd}
                renderPod={renderListPod}
                renderPostGroupItems={renderIntentInput}
                renderGroupButtons={renderGroupButtons}
                renderSubtitle={renderSubtitle}
                renderActionButtons={customActionButtons}
                stickyRenderActionButtons={customStickyActionButtons}
                renderProductLoader={renderProductLoader}
                showNumbering={false}
                mobileActionBar={isMobile}
                showHorizontalDragHandleContainer={isMobile} // isMobile when mobile drag enabled
                isLoading={productInfoLoading && productsDisplayed.length > 0}
                disableDragAndDrop={isMobile || isSharedList}
                renderDeleteButton={isMobile ? renderDeleteButton : undefined}
                groupToBeAutoOpened={groupToBeAutoOpened}
                onExpandAll={onExpandAll}
                onCollapseAll={onCollapseAll}
                onGroupExpand={onGroupExpand}
                onSelectAllGroupsAndItems={handleSelectAllGroupsAndItems}
                onSelect={handleSelectionChange}
                disableStickyActionBar={!hasItemsSelected}
                hideSelectAll
                errorSelectionMessage={errorSelectionEmpty}
                disablePlaceholders
                stickyActionBarToBottom
                enableHeaderOffset
              />
            )
          }
        </ListGroupWrapper>
      </QueryProvider>
      <div className="sui-w-full sui-flex sui-justify-center sui-items-center sui-pb-6">
        <QueryProvider
          cacheKey={`list-add-to-materials-below-group-${list?.id || 'default'}`}
          defaultVariables={defaultVariable}
        >
          <AddTo
            addToType="List"
            defaultOpen={isEmptyList}
            list={{
              listId: list?.id,
              onMaterialsAdded: onSuccesfulMaterialsAdd,
              isMaterialsButton: true
            }}
            CustomAddToActionElement={({ onClick }) => (
              <div className={buttonWrapperClass}>
                <Button
                  aria-label="Add Materials"
                  variant="primary"
                  onClick={onClick}
                  text="Add Materials"
                  disabled={isSharedList}
                >
                  Add Materials
                </Button>
              </div>
            )}
            aiListBuilderData={{
              onNewIntentsSave: handleIntentsChange,
              title: list?.name,
              materials,
              refType: 'LIST',
              refId: list?.id
            }}
            products={[]}
          />
        </QueryProvider>
      </div>
    </div>
  );
};

ListView.dataModel = extend(
  {
    listOperations: params({
      operationName: customType('Operation').enum(
        ['Delete', 'RemoveItems', 'UpdateItems', 'Create', 'Update', 'AddItems'], 'Create'),
      id: stringType().isRequired(),
      groups: arrayOf(customType('GroupInput').shape({
        groupId: stringType(),
        groupName: stringType(),
        groupOrder: stringType(),
        products: arrayOf(customType('ProductInput').shape({
          itemId: stringType(),
          quantity: numberType(),
          itemOrder: numberType(),
          comments: stringType(),
          intent: stringType()
        }))
      })),
      name: stringType(),
      listAccessType: customType('AccessTypes').enum(['Shared', 'Private'])
    }).mutation().shape({
      id: stringType(),
      groups: arrayOf(shapeType({
        groupId: stringType(),
        status: stringType(),
        items: arrayOf(shapeType({
          itemId: stringType(),
          quantity: numberType(),
          itemOrder: numberType(),
          comments: stringType(),
          status: stringType(),
          intent: stringType(), // NEW: echo of intent from backend
          intentId: stringType() // NEW: server-generated intent id
        }))
      }))
    }),
  },
  ListPod, ListPodMobile, IntentPod, ActionBar, AddTo, CreateGroup, GroupSubTitle, EditGroup
);

ListView.displayName = 'ListView';

ListView.propTypes = {
  storeId: string,
  skipFn: func,
  productDefaultVariables: propShape({}),
  mountedFn: func,
  setIntersecting: func.isRequired,
  groupTotals: propShape({}).isRequired,
  setGroupTotals: func.isRequired,
};

ListView.defaultProps = {
  storeId: '',
  skipFn: () => { },
  mountedFn: () => { },
  productDefaultVariables: {},
};
